import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useCreateSignatureMutation } from '../app/athenaApi';
import {
  athenaApi,
  usePostApiWorkflowsByIdTasksAndTaskNumberApproveMutation,
  usePostApiWorkflowsByIdTasksAndTaskNumberRejectMutation,
} from '../app/athenaApiGen';
import { useSigner } from './signerProvider';
import { useCacSimulation } from './simulation/CacSimulationProvider';
import { SimulationError } from './simulation/types';
import {
  SIGNING_ERROR_CODES,
  type MintArgs,
  type SignableAction,
  describeSigningError,
} from './types';

export type SignAndSubmitStatus =
  | 'idle'
  | 'submitting'
  | 'signing'
  | 'verifying'
  | 'submitting-signed'
  | 'success'
  | 'error'
  // Surfaced only when the simulated signer is switched off
  // (SIMULATED_SIGNING_ENABLED=false) and no real signer is wired yet — a
  // signature-required task then shows a "Work in Progress" notice. While the
  // demo switch is on, the simulator runs in every environment and this state
  // is unused.
  | 'wip';

export type SignAndSubmitState = {
  status: SignAndSubmitStatus;
  errorCode?: string;
  errorMessage?: string;
};

export type SubmitArgs = {
  workflowId: string;
  taskNumber: number;
  comments: string;
  // Whether this task requires a cryptographic signature, derived from the
  // backend signing policy in the workflow-types catalog
  // (GET /api/workflows/types → signingRequirements[].required; see
  // useSignatureRequirement). When false/omitted, the approve/reject is
  // submitted with no signature and makes NO /api/dev/sign-cms call. When
  // true, the approve action additionally routes through the simulated CAC
  // credential dialog before signing. Consulted for both the approve and
  // reject actions.
  signatureRequired?: boolean;
};

// isTarWorkflow detects TAR workflows by approval-queue objectType ("TAR") or a
// TAR marker in the workflow type name (e.g. the IMCS TAR With/Without APM
// types).
//
// NOTE: not used by the signing flow today — the signing gate is catalog-driven
// (SubmitArgs.signatureRequired, from useSignatureRequirement). Retained for the
// future: if TAR ever needs workflow-type-specific UI/handling again (e.g. a
// TAR-only signing-GUI preview while the catalog still reports no signature
// required), wire this back into submit(). See git history for the prior
// "simulate TAR signing GUI" exception.
export function isTarWorkflow(args: {
  objectType?: string;
  workflowType?: string;
}): boolean {
  if (args.objectType?.trim().toUpperCase() === 'TAR') return true;
  return /tar/i.test(args.workflowType ?? '');
}

const APPROVAL_INVALIDATION_TAGS = [
  'Deliverables',
  'Projects',
  'CTP (Consent to Purchase)',
  'PNR (Problem Notification Report)',
  'RIP (Request to Initiate Purchase)',
  'TAR (Travel Authorization Request)',
  'TR (Trip Report)',
  'Approval Queue',
  'Audit Logs',
] as const;

type UnknownError = {
  status?: number;
  data?: {
    code?: string | number;
    message?: string;
    error?: string;
    details?: string;
  };
};

// The workflow approve/reject handler routes signing errors through
// util.RespondWithError, which swallows the symbolic code whenever a
// `details` string is supplied (services/internal/util/util.go:36-50).
// Until that helper is replaced with a code-preserving response, we
// recognize each known sentinel message and remap it to the symbolic
// code the handler intended to send. Patterns mirror the literal strings
// in services/internal/api/handlers/workflow_handlers.go and
// workflow_signing.go.
const DETAILS_TO_CODE: Array<{ test: RegExp; code: string }> = [
  {
    test: /requires a cryptographic signature/i,
    code: SIGNING_ERROR_CODES.SIGNATURE_REQUIRED,
  },
  {
    test: /signer does not match actor|actor has no Contact/i,
    code: SIGNING_ERROR_CODES.IDENTITY_MISMATCH,
  },
  {
    test: /no signature with the given id/i,
    code: SIGNING_ERROR_CODES.SIGNATURE_NOT_FOUND,
  },
  {
    test: /state is .* want Verified/i,
    code: SIGNING_ERROR_CODES.SIGNATURE_NOT_VERIFIED,
  },
  {
    test: /does not cover this .*tuple|has no usable payloadDigest/i,
    code: SIGNING_ERROR_CODES.PAYLOAD_REBIND_MISMATCH,
  },
  {
    test: /CertType.*not in allowed list|signature has no CertType/i,
    code: SIGNING_ERROR_CODES.CERT_TYPE_NOT_ALLOWED,
  },
];

function extractErrorCode(err: unknown): { code: string; message: string } {
  const e = err as UnknownError | undefined;
  const rawCode = e?.data?.code;
  const detailsText = e?.data?.details ?? e?.data?.message ?? e?.data?.error;

  let code: string;
  if (typeof rawCode === 'string') {
    code = rawCode;
  } else {
    const matched = DETAILS_TO_CODE.find((entry) =>
      detailsText ? entry.test.test(detailsText) : false,
    );
    code = matched?.code ?? SIGNING_ERROR_CODES.UNKNOWN;
  }

  return { code, message: describeSigningError(code, detailsText) };
}

export function useSignAndSubmit() {
  const dispatch = useDispatch();
  const signer = useSigner();
  const cacSim = useCacSimulation();
  const [approveTask] =
    usePostApiWorkflowsByIdTasksAndTaskNumberApproveMutation();
  const [rejectTask] =
    usePostApiWorkflowsByIdTasksAndTaskNumberRejectMutation();
  const [createSignature] = useCreateSignatureMutation();

  const [state, setState] = useState<SignAndSubmitState>({ status: 'idle' });

  const reset = useCallback(() => setState({ status: 'idle' }), []);

  const submit = useCallback(
    async (action: SignableAction, args: SubmitArgs) => {
      const { workflowId, taskNumber } = args;
      const comments = args.comments;

      const callAction = (signatureId?: string) => {
        if (action === 'approve') {
          return approveTask({
            id: workflowId,
            taskNumber,
            handlersTaskApprovalRequest: { comments, signatureId },
          }).unwrap();
        }
        return rejectTask({
          id: workflowId,
          taskNumber,
          handlersTaskRejectionRequest: { comments, signatureId },
        }).unwrap();
      };

      const finish = () => {
        dispatch(athenaApi.util.invalidateTags([...APPROVAL_INVALIDATION_TAGS]));
        setState({ status: 'success' });
      };

      // Mint a CMS signature, persist it, then call approve/reject exactly
      // once with the resulting signatureId. This is the only place the
      // approve/reject endpoint is hit on the signing path.
      const mintAndSubmit = async () => {
        let signatureId: string | undefined;
        try {
          setState({ status: 'signing' });
          const mintArgs: MintArgs = {
            workflowId,
            taskNumber,
            action,
            comments,
          };
          const minted = await signer.sign(mintArgs);

          setState({ status: 'verifying' });
          const sigResp = await createSignature({
            cms: minted.cms,
            payloadDigest: minted.payloadDigest,
            payloadType: minted.payloadType,
          }).unwrap();
          signatureId = sigResp.signatureId;
          if (!signatureId) {
            setState({
              status: 'error',
              errorCode: SIGNING_ERROR_CODES.INTERNAL_ERROR,
              errorMessage: describeSigningError(
                SIGNING_ERROR_CODES.INTERNAL_ERROR,
              ),
            });
            return;
          }
        } catch (err) {
          const { code, message } = extractErrorCode(err);
          setState({ status: 'error', errorCode: code, errorMessage: message });
          return;
        }

        try {
          setState({ status: 'submitting-signed' });
          await callAction(signatureId);
          finish();
        } catch (err) {
          const { code, message } = extractErrorCode(err);
          setState({ status: 'error', errorCode: code, errorMessage: message });
        }
      };

      // Run the simulated credential picker dialog (CAC PIN / Windows
      // Security, including the Windows cert picker) to completion. Returns
      // true to proceed with minting, false if the user cancelled or the
      // simulation errored (in which case state has already been set).
      const runCredentialDialog = async (): Promise<boolean> => {
        try {
          await cacSim.openSession({ action });
          return true;
        } catch (err) {
          if (err instanceof SimulationError) {
            if (err.code === 'USER_CANCELLED') {
              setState({ status: 'idle' });
              return false;
            }
            setState({
              status: 'error',
              errorCode: err.code,
              errorMessage: describeSigningError(err.code),
            });
            return false;
          }
          setState({
            status: 'error',
            errorCode: SIGNING_ERROR_CODES.UNKNOWN,
            errorMessage: describeSigningError(SIGNING_ERROR_CODES.UNKNOWN),
          });
          return false;
        }
      };

      // The simulated signer: the /api/dev/sign-cms minter (ephemeral test CA)
      // plus the simulated CAC dialog. Real CAC / Windows signing is not
      // implemented yet (see useSigningMode.ts 'cac-native' / 'mtls').
      //
      // This is intentionally NOT gated on import.meta.env.DEV: the simulator
      // runs identically in local dev and in deployed demo builds, controlled
      // solely by this explicit demo switch. It must stay in lockstep with the
      // backend common.SimulatedSigningEnabled() gate
      // (services/internal/common/config.go), which is likewise decoupled from
      // DEV_MODE.
      //
      // ⚠️ TEMPORARY DEMO SCAFFOLDING — REMOVE WHEN REAL SIGNING SHIPS. ⚠️
      // Removal plan:
      //   1. Implement a real signer in signerProvider.ts (CAC native-messaging
      //      bridge or Windows cert store) and select it via useSigningMode.ts.
      //   2. Flip SIMULATED_SIGNING_ENABLED to false here AND
      //      simulatedSigningHardcoded to false in the backend config.go.
      //   3. With the simulator off, the WIP notice below is shown again until
      //      the real signer is wired in.
      // The simulated CAC dialog already labels itself "Simulated signing"
      // (CacSimulationDialog.tsx), so testers/customers can tell it is a demo.
      const SIMULATED_SIGNING_ENABLED = true;

      // Signing policy is owned by the backend and surfaced per workflow type
      // in the catalog (GET /api/workflows/types → signingRequirements[].required;
      // see useSignatureRequirement). When a signature is not required for this
      // task, approve/reject is submitted with no signature — no signer is
      // invoked and NO /api/dev/sign-cms request is made. The catalog set is
      // empty under #1255, so this is the path for every workflow type today;
      // re-enabling signing for a type (#1179) flips this with no UI change.
      if (!args.signatureRequired) {
        setState({ status: 'submitting' });
        try {
          await callAction();
          finish();
        } catch (err) {
          const { code, message } = extractErrorCode(err);
          setState({ status: 'error', errorCode: code, errorMessage: message });
        }
        return;
      }

      if (!SIMULATED_SIGNING_ENABLED) {
        setState({ status: 'wip' });
        return;
      }

      // Signature-required approve: open the simulated credential-picker dialog
      // (CAC PIN / Windows Security, including the Windows cert picker) before
      // signing, then mint + submit the signature.
      if (action === 'approve') {
        setState({ status: 'submitting' });
        if (!(await runCredentialDialog())) return;
        await mintAndSubmit();
        return;
      }

      // Signature-required reject: never opens the credential-picker GUI (that
      // is an approve-only concern); mints + submits a signature headlessly.
      setState({ status: 'submitting' });
      await mintAndSubmit();
    },
    [approveTask, rejectTask, createSignature, signer, dispatch, cacSim],
  );

  const submitApproval = useCallback(
    (args: SubmitArgs) => submit('approve', args),
    [submit],
  );
  const submitRejection = useCallback(
    (args: SubmitArgs) => submit('reject', args),
    [submit],
  );

  return { state, reset, submitApproval, submitRejection };
}
